import React, { useEffect, useState, useCallback } from 'react';
import { UsersIcon, CatechesisGrid, SearchBar, LoadMoreButton } from '../components';
import { Skeleton } from '../components/ui/skeleton';
import { fetchCatecheticalCourses, toggleFavorite } from '../utilities/api';
import { mapCatecheticalCoursesListData } from '../utilities';

const decodeHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.documentElement.textContent || doc.body.textContent;
};
const PageLearnTheFaith = () => {
  const [catechesisData, setCatechesisData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const itemsPerPage = 12;
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchItems, setSearchItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch Initial Data
  useEffect(() => {
    console.log("Fetching data with searchQuery:", searchQuery);
    
    const loadCourses = async () => {
      try {
        setLoading(true);
        setError(null);
        setCatechesisData([]);
        const response = await fetchCatecheticalCourses(0, itemsPerPage , searchQuery);
        const transformedData = mapCatecheticalCoursesListData(response.data || []);
        setCatechesisData(transformedData);
        // setCatechesisData(updatedData);
        setSearchItems(transformSearchData(response.data || []));
        setOffset(itemsPerPage);
        setTotalRecords(response.meta?.pagination?.total || 0);
        setHasMore(itemsPerPage < (response.meta?.pagination?.total || 0));
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };
    loadCourses();
  }, [searchQuery]);

  // Load More Function
  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const response = await fetchCatecheticalCourses(offset, itemsPerPage , searchQuery);
      const transformedData = mapCatecheticalCoursesListData(response.data || []);
      setCatechesisData(prev => [...prev, ...transformedData]);
      setOffset(prev => prev + itemsPerPage);
      const newTotal = response.meta?.pagination?.total || 0;
      setHasMore(offset + itemsPerPage < newTotal);
    } catch (error) {
      console.error("Error loading more data:", error);
      setError("Failed to load more items.");
    } finally {
      setLoadingMore(false);
    }
  };

  const transformSearchData = useCallback((apiData) =>
    (apiData || []).map(item => ({
      id: item.id || item.ID,
      name: decodeHtml(item.attributes.name || 'Untitled')
    })), []);

  const handleToggleFavorite = async (id) => {
    try {
      const currentFavorite = catechesisData.find(item => item.id === id)?.favorite;
      // Step 1: Ensure prevData is an array before modifying
      setCatechesisData((prevData) => {
        if (!Array.isArray(prevData)) {
          console.error(" Error: prevData is not an array. Resetting state.");
          return [];
        }
        return prevData.map((item) =>
          item.id === id ? { ...item, favorite: !item.favorite } : item
        );
      });

      // Step 3: Call API (Do NOT reload everything)
      await toggleFavorite('catechetical-courses', id, true,currentFavorite);
    } catch (error) {
      console.error("Failed to update favorite:", error);
      //  Step 4: If API call fails, revert UI
      setCatechesisData((prevData) =>
        prevData.map((item) =>
          item.id === id ? { ...item, favorite: !item.favorite } : item
        )
      );
    }
  };
  const handleSearchQuery = (value) => {
    console.log("Search Query:", value);
    
    setSearchQuery(value);  // Ensure state updates
  };
  return (
    <div className="w-full">
      <div className="px-4 py-8 pl-6 xl:px-6">
        <div className="mb-4 md:mb-6">
          <div className="flex flex-wrap items-center justify-between gap-6 md:gap-3">
            <div className="flex items-center gap-2">
              <UsersIcon className="w-6 h-6 text-gold-600" />
              <h1 className="text-2xl font-bold text-gray-900 md:text-xl xl:text-2xl">
                Learn the Faith
              </h1>
            </div>
            <SearchBar
              inputName="s"
              inputPlaceholder="Search by name"
              items={searchItems} // Pass filtered search items          
              onSearch={handleSearchQuery} // Updates search query
            />
          </div>
        </div>
        {/* Show Skeleton While Loading */}
        {loading ? (
          <div className="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: itemsPerPage }).map((_, index) => (
              <Skeleton key={index} className="h-[228px] w-full rounded-lg" />
            ))}
          </div>
        ) : error ? (
          <div className="p-4 text-center text-red-500">{error}</div>
        ) : (
          <>
            <CatechesisGrid section={{ items: catechesisData }} searchQuery={searchQuery} onToggleFavorite={handleToggleFavorite} columns={4} />
            {/* Load More Button (Hidden if No More Data) */}
            {hasMore && catechesisData.length < totalRecords && (
              <div className="flex justify-center mt-6">
                <LoadMoreButton
                  label="Load More"
                  onClick={handleLoadMore}
                  isLoading={loadingMore}
                  disabled={loadingMore}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
export default PageLearnTheFaith;
